import originalAxios from 'axios';
import { queryClient } from '@/queryClient';
import config from '@/config';
import { AppRoutes } from '@/routes';
import axios, { getRequestHeaderParams } from '@/axios-api';
import { useUserStore } from '@/store/userStore';
import Pathes from '@/helpers/pathes';
import { getMessage } from '@/helpers/utils';
import { clearRefreshSessionHint } from '@/helpers/authSession';
import { APP_LINKS } from '@/helpers/links';
import { ChatwootChat } from '@/chat/chatwoot';
import { LOGOUT_USER } from '@/store/actions/actionTypes';
import store from '@/store/rootStore';
import { discardInFlightRefresh } from '@/store/tokenRefresh';

export const getFounderDetail = id => {
  return axios
    .get(Pathes.Founders.detail(id))
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        return { data, success: true };
      }
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const getStartupSectors = () => {
  return axios
    .get(Pathes.User.sectors)
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        return { data, success: true };
      }
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

// Invite code generation
export const generateCoFounderInviteCode = () => {
  return axios
    .post(Pathes.Auth.coFounderInvite)
    .then(response => {
      const { status, data } = response;
      if (status === 200) {
        return { data, success: true };
      }
      throw new Error('Could not generate invite link');
    })
    .catch(e => ({ success: false, message: e.message }));
};

// Validate invite code
export const validateCoFounderInviteCode = code => {
  return axios
    .post(Pathes.Auth.coFounderValidateInvite, { code })
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        return { success: true };
      }
      if (status > 401 && status < 500) {
        return { success: false, message };
      }
      throw new Error(message);
    })
    .catch(() => ({ success: false }));
};

export const clearLocalSession = ({ redirect = true } = {}) => {
  discardInFlightRefresh();
  ChatwootChat.reset();
  clearRefreshSessionHint();
  useUserStore.getState().clearTokens();
  queryClient.clear();
  sessionStorage.clear();
  store.dispatch({ type: LOGOUT_USER });

  if (redirect) {
    AppRoutes.navigate(APP_LINKS.signIn);
  }
};

// Dedupe concurrent callers: several parallel 401s used to fire one
// POST /logout/ each, and the reload masked it.
let logoutInFlight = null;

export const logoutUser = () => {
  if (logoutInFlight) return logoutInFlight;

  discardInFlightRefresh();

  logoutInFlight = originalAxios
    .post(`${config.apiURL}${Pathes.Auth.logout}`, undefined, {
      withCredentials: true,
      headers: getRequestHeaderParams(),
    })
    .catch(() => {
      // logout is best-effort: local state must be cleared either way
    })
    .finally(() => {
      try {
        clearLocalSession();
      } catch (e) {
        console.error(e);
      } finally {
        logoutInFlight = null;
      }
    });

  return logoutInFlight;
};